import React from 'react'

import {
  Box,
  Bold,
  Body2,
  Color,
  Display,
  FlexDirection,
  AlignItems,
  JustifyContent,
  FontWeight,
  GridDisplay,
  Overflow,
  Text,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { CWCNewBadge } from '@library/landing-pages'

import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { CheckoutButton } from 'routes/Checkout/Components/CheckoutButton'
import { signupGoToMenu } from 'routes/Signup/signupActions'

import {
  BENEFITS,
  CTA_TEXT,
  DESKTOP_PLATES,
  MOBILE_PLATES,
  TABLET_PLATES,
  type BenefitSegment,
} from './config'

// A plate fills a fixed square cell via a CSS background-image rather than an <img>:
// images inside a `display:none` block are NOT fetched by the browser, so mobile/tablet
// don't download the hidden desktop grid's plates (an <img> loads regardless of display).
// `minWidth` stops the cell shrinking so the rows keep their width and bleed off-screen
// (clipped by the root); gaps between plates come from the assets' own transparent margins.
// The plates are decorative, so there is no alt text to lose by dropping the <img>.
const PlateCell = ({ src, size }: { src: string; size: string }) => (
  <Box
    width={size}
    minWidth={size}
    height={size}
    style={{
      backgroundImage: `url(${src})`,
      backgroundSize: 'contain',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  />
)

// On mobile/tablet the plates overlap by 12px (mr-[-12px] in Figma); apply the
// negative margin to every cell after the first so the row reads as a cluster.
const PlateRow = withCitrusTheme(Box, () => ({
  '& > * + *': { marginLeft: '-12px' },
}))

const CTAContainer = withCitrusTheme(Box, () => ({
  '& button': { borderRadius: '8px' },
}))

const BenefitBody = ({ segments }: { segments: BenefitSegment[] }) => (
  <Body2 size={4} color={Color.ColdGrey_800}>
    {segments.map((segment, index) =>
      segment.bold ? (
        // eslint-disable-next-line react/no-array-index-key
        <Bold key={index}>{segment.text}</Bold>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <Text key={index} display={Display.Inline}>
          {segment.text}
        </Text>
      ),
    )}
  </Body2>
)

export const CwCInterstitialPage = () => {
  const dispatch = useDispatch()

  return (
    <Box
      data-testing="cwcInterstitialPage"
      bg={Color.White}
      width="100%"
      flexGrow={1}
      overflowX={Overflow.Hidden}
      display={Display.Flex}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
      alignItems={AlignItems.Center}
      justifyContent={[JustifyContent.FlexStart, null, null, JustifyContent.Center]}
      gap={[6, null, null, 18]}
      paddingV={[6, null, 8]}
    >
      {/* Mobile: 3 plates (hidden from the tablet breakpoint up) */}
      <PlateRow
        display={[Display.Flex, null, Display.None]}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
      >
        {MOBILE_PLATES.map((src) => (
          <PlateCell key={src} src={src} size="200px" />
        ))}
      </PlateRow>

      {/* Tablet: 5 plates (only between the tablet and desktop breakpoints) */}
      <PlateRow
        display={[Display.None, null, Display.Flex, Display.None]}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
      >
        {TABLET_PLATES.map((src) => (
          <PlateCell key={src} src={src} size="200px" />
        ))}
      </PlateRow>

      {/* Desktop: 3×4 grid of 12 plates */}
      <Box
        display={[Display.None, null, null, GridDisplay.Grid]}
        gridTemplateColumns="repeat(3, 190px)"
      >
        {DESKTOP_PLATES.map((src) => (
          <PlateCell key={src} src={src} size="190px" />
        ))}
      </Box>

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={6}
        maxWidth="426px"
        width={['100%', null, null, '384px']}
        paddingH={[3, null, 0]}
      >
        <Box
          display={Display.Flex}
          alignItems={AlignItems.Center}
          gap={1}
          role="heading"
          aria-level={1}
        >
          <Text size={10} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
            Meet the
          </Text>
          {/* The sticker is decorative (aria-hidden); "New" is kept as real, visually-hidden
              text so assistive tech hears "Meet the New Gousto" — matching the CWCHero pattern. */}
          <VisuallyHidden>New </VisuallyHidden>
          <CWCNewBadge width="67px" height="48px" />
          <Text size={10} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
            Gousto
          </Text>
        </Box>

        {BENEFITS.map((benefit) => (
          <Box
            key={benefit.heading}
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={1}
          >
            <Text size={5} fontWeight={FontWeight.Bold} color={Color.Primary_600}>
              {benefit.heading}
            </Text>
            <BenefitBody segments={benefit.body} />
          </Box>
        ))}

        <CTAContainer width="100%">
          <CheckoutButton
            onClick={() => dispatch(signupGoToMenu())}
            testingSelector="cwcInterstitialCTA"
          >
            {CTA_TEXT}
          </CheckoutButton>
        </CTAContainer>
      </Box>

      <RibbonTriggerContainer name="wizard-proposition" />
    </Box>
  )
}
